import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  handleInputChangeWithScroll,
  MESSAGE_TYPES,
} from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  executeValidationWithArrays,
  handleInputChangeWithValidation,
  handleMediaSelectWithValidation,
  handleArrayInputChangeWithValidation,
  handleArrayMediaSelectWithValidation,
  handleArrayItemRemoveWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "About", path: `/about` },
];

function AboutEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "aboutContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    page_title: "",
    page_image: {},
    page_content: "",
    catchcopy: "",
    about_items: [],
  });

  // ドラッグ&ドロップ並べ替え処理
  const handleAboutItemReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          about_items: reorderedItems,
        };

        clearValidationErrors();

        // iframeに全データを送信
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
              isFullUpdate: true,
            },
            process.env.REACT_APP_ORIGIN
          );

          // 確認のため、少し遅延してもう一度送信
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                allData: newData,
                isFullUpdate: true,
              },
              process.env.REACT_APP_ORIGIN
            );
          }, 100);
        }

        return newData;
      });
    },
    [contentKey]
  );

  // ドラッグ&ドロップフック
  const { getDraggableProps } = useDragAndDrop(
    pageContent.about_items || [],
    handleAboutItemReorder
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_about_content/`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      console.log("data", data);
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // バリデーションルールを定義
  const validationRules = {
    page_title: { required: true },
    page_content: { required: true },
  };

  const arrayValidationRules = {
    about_items: {
      title: { required: true },
      text: { required: true },
    },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    page_title: "ページタイトル",
    page_content: "ページ本文",
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    const formData = { page_content: pageContent };

    // バリデーション実行
    if (
      !executeValidationWithArrays(
        pageContent,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_about_content/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setMessage(data.message);
      await fetchPageContentData(); // 最新データ再取得

      // 成功時にページトップまでスクロール
      setTimeout(() => {
        const editArea = document.querySelector(".editarea");
        if (editArea) {
          editArea.scrollTop = 0;
        }
      }, 100);

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      setMessage("更新に失敗しました");
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  // 入力変更処理（単一フィールド用）
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
  };

  // 繰り返し要素（about_items）の入力変更
  const handleAboutItemInputChange = (e, index, fieldName) => {
    handleArrayInputChangeWithValidation(
      e,
      index,
      fieldName,
      setPageContent,
      "about_items",
      (newData, index, fieldName, value, eventType) => {
        // iframe通信処理
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          const targetClass = `.edit_about_item${index}_${fieldName}`;
          const messageType =
            eventType === "focus"
              ? MESSAGE_TYPES.SCROLL_TO
              : MESSAGE_TYPES.TEXT_UPDATE;
          iframe.contentWindow.postMessage(
            {
              type: messageType,
              target: targetClass,
              contentKey,
              fieldName,
              index,
              value,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  // 繰り返し要素（about_items）の画像変更
  const handleAboutItemImageChange = (index, fieldName, media) => {
    handleArrayMediaSelectWithValidation(
      index,
      fieldName,
      media,
      setPageContent,
      "about_items",
      (newData, index, fieldName, media) => {
        // iframe通信処理
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          const targetClass = `.edit_about_item${index}_${fieldName}`;
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.IMAGE_UPDATE,
              target: targetClass,
              contentKey,
              fieldName,
              index,
              value: media,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  // 繰り返し要素（about_items）の追加
  const addAboutItem = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newItem = {
        title: "",
        text: "",
        image1: {},
        image2: {},
        image3: {},
      };

      let newAboutItems;
      if (insertAfterIndex !== null) {
        // 指定位置に挿入
        newAboutItems = [
          ...prev.about_items.slice(0, insertAfterIndex + 1),
          newItem,
          ...prev.about_items.slice(insertAfterIndex + 1),
        ];
      } else {
        // 末尾に追加
        newAboutItems = [...prev.about_items, newItem];
      }

      const newData = {
        ...prev,
        about_items: newAboutItems,
      };

      // iframeに全データを送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // 繰り返し要素（about_items）の削除
  const removeAboutItem = (index) => {
    handleArrayItemRemoveWithValidation(
      index,
      setPageContent,
      "about_items",
      arrayValidationRules.about_items,
      fieldLabels,
      (newData) => {
        // iframe通信処理
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              allData: newData,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }
    );
  };

  // メディア選択処理
  const handleMediaSelect = (fieldName, media) => {
    handleMediaSelectWithValidation(fieldName, media, setPageContent);

    // iframe通信処理
    setPageContent((prev) => {
      const newData = {
        ...prev,
        [fieldName]: media,
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_${fieldName}`;
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            value: media,
            allData: newData,
          },
          process.env.REACT_APP_ORIGIN
        );
      }

      return newData;
    });
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      setLoadingText("アップロード中");
      // ドラッグ&ドロップまたはファイル選択の両方に対応
      const files = e.dataTransfer?.files || e.target?.files;
      const file = files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        await fetchMediaFiles();
        if (data.images?.[0]) {
          selectMedia(data.images[0]);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  // スタイル要素を作成
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
        .loop {
          grid-template-columns: auto !important;
        }
      `;

    // head に追加
    document.head.appendChild(styleElement);

    // クリーンアップ関数
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    console.log("pageContent", pageContent);
  }, [pageContent]);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* ページ上部 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <input
                        type="text"
                        name="page_title"
                        value={pageContent.page_title}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="ページタイトル"
                        data-required="true"
                      />
                      <textarea
                        className="input2"
                        name="page_content"
                        value={pageContent.page_content}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="ページ本文"
                        data-required="true"
                      />
                      <input
                        type="text"
                        name="catchcopy"
                        value={pageContent.catchcopy}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        placeholder="キャッチコピー"
                        data-label="キャッチコピー"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="ページ画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="page_image"
                          selectedMedia={pageContent.page_image}
                          onSelect={(media) =>
                            handleMediaSelect("page_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 繰り返し */}
              <section>
                <div className="area2">
                  {pageContent.about_items?.map((about_item, index) => (
                    <div
                      className="item"
                      key={index}
                      {...getDraggableProps(index)}
                    >
                      <div className="ctrl">
                        <p>{index + 1}</p>
                        <div className="icon-wrap">
                          <i
                            className="icon delete"
                            onClick={() => removeAboutItem(index)}
                          ></i>
                          <i
                            className="icon add"
                            onClick={() => addAboutItem(index)}
                          ></i>
                        </div>
                      </div>
                      <div
                        className="forms loop"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          name={`about_item${index}_title`}
                          value={pageContent.about_items[index]?.title ?? ""}
                          onChange={(e) =>
                            handleAboutItemInputChange(e, index, "title")
                          }
                          onFocus={(e) =>
                            handleAboutItemInputChange(e, index, "title")
                          }
                          placeholder="タイトル"
                          data-label="タイトル"
                          data-required="true"
                        />
                        <textarea
                          className="input2"
                          name={`about_item${index}_text`}
                          value={pageContent.about_items[index]?.text ?? ""}
                          onChange={(e) =>
                            handleAboutItemInputChange(e, index, "text")
                          }
                          onFocus={(e) =>
                            handleAboutItemInputChange(e, index, "text")
                          }
                          placeholder="説明"
                          rows={5}
                          data-label="説明"
                          data-required="true"
                        />
                        <div className="img-form">
                          <MediaSelector
                            label="画像1"
                            aspectRatio="9:16"
                            type="image"
                            fieldName={`about_item${index}_image1`}
                            selectedMedia={about_item.image1}
                            onSelect={(media) =>
                              handleAboutItemImageChange(index, "image1", media)
                            }
                          />
                          <MediaSelector
                            label="画像2"
                            aspectRatio="9:16"
                            type="image"
                            fieldName={`about_item${index}_image2`}
                            selectedMedia={about_item.image2}
                            onSelect={(media) =>
                              handleAboutItemImageChange(index, "image2", media)
                            }
                          />
                          <MediaSelector
                            label="画像3"
                            aspectRatio="9:16"
                            type="image"
                            fieldName={`about_item${index}_image3`}
                            selectedMedia={about_item.image3}
                            onSelect={(media) =>
                              handleAboutItemImageChange(index, "image3", media)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/about" data={pageContent} contentKey={contentKey} />
    </>
  );
}

export default function AboutEditPage() {
  return (
    <MediaProvider>
      <AboutEditPageContent />
    </MediaProvider>
  );
}
